import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DEFAULT_CALENDAR_STYLE, decorFor, emptyCounts, fieldGridCss, gridScope, ornamentFor, } from '../field-state/calendar-design.js';
import { FieldTile } from './FieldTile.js';
/**
 * The calendar grid.
 *
 * An unordered list, because the list order is the mosaic (`arrangeFields`), not the order
 * of the days: a screen reader announces "Liste mit 24 Einträgen" and each door by its
 * number, which is what a participant looks for. The markup order is the drawn order, so
 * the keyboard walks the mosaic the way the eye reads it. The honeycomb keeps that order and
 * fills its rows from the left, so the same holds there without exception.
 *
 * The frame is the container the grid measures itself against: a cell is a share of its
 * width, so doors keep their ratio at every width without a breakpoint on the viewport, and
 * the column count follows the space the grid actually has (concept 9.6.5, mobile first).
 *
 * A configured grid (D29) brings its own style sheet, scoped to this frame, and closes the
 * last row with empty places. The action may mix more empty places in among the doors (D31,
 * `arrangeFields` with `fillers`). Every empty place is hidden from assistive technology and
 * carries at most a drawn shape (`ornaments.css`): there is nothing in it to read.
 *
 * THE LOOK (D31) travels as data attributes on the frame, one per setting; field.css and
 * ornaments.css draw from them. Nothing of it is a state, so no state text depends on it.
 *
 * Fields without a size are single cells. The backoffice preview relies on that: it shows
 * the states, not a mosaic.
 */
export function FieldGrid({ fields, fieldTerm = 'field', shape = 'square', layout = 'mosaic', grid = null, numberStyle = null, calendarStyle = null, label, className, }) {
    const style = calendarStyle ?? DEFAULT_CALENDAR_STYLE;
    const honeycomb = layout === 'honeycomb';
    const tileShape = honeycomb ? 'hexagon' : shape;
    const classNames = [
        'countdown-field-grid',
        honeycomb ? 'countdown-field-grid--honeycomb' : null,
        className,
    ]
        .filter(Boolean)
        .join(' ');
    const cellCount = fields.reduce((cells, field) => cells + (honeycomb || field.size === undefined || field.size === 'single' ? 1 : 2), 0);
    const empties = grid === null ? 0 : Math.max(...emptyCounts(grid, cellCount));
    const scope = grid === null ? undefined : gridScope(layout, grid, cellCount);
    const fillerCount = fields.filter((field) => 'filler' in field).length;
    // Every empty place, in among the doors or closing the last row, is the same element: the
    // ground of the frame, and a shape on it where the action wants one.
    const emptyPlace = (key, index, size, extra) => (_jsx("li", { className: [
            'countdown-field',
            'countdown-field--empty',
            `countdown-field--shape-${tileShape}`,
            honeycomb || size === undefined || size === 'single' ? null : `countdown-field--${size}`,
        ]
            .filter(Boolean)
            .join(' '), "data-ornament": style.filler_ornaments ? ornamentFor(index) : undefined, "aria-hidden": "true", ...extra, children: _jsx("div", { className: "countdown-field__body" }) }, key));
    return (_jsxs("div", { className: "countdown-field-grid-frame", "data-countdown-grid": scope, "data-door-opening": style.door_opening, "data-door-look": style.door_look, "data-today-highlight": style.today_highlight, "data-past-marker": style.past_marker, children: [grid !== null && scope !== undefined ? (
            // Raw on purpose: React would escape the child combinator `>`. Every value in it is
            // a bounded integer or the stripped scope (fieldGridCss), nothing a visitor typed.
            _jsx("style", { dangerouslySetInnerHTML: { __html: fieldGridCss({ scope, layout, grid, cellCount }) } })) : null, _jsxs("ul", { className: classNames, "aria-label": label, children: [fields.map((field, index) => 'filler' in field ? (emptyPlace(`filler-${String(field.filler)}`, field.filler, field.size, {})) : (_jsx(FieldTile
                    // The index too: a preview shows one door in several states.
                    , { ...field, numberStyle: field.numberStyle ?? numberStyle, numberWobble: style.number_wobble, decor: style.decorations ? decorFor(field.position) : null, fieldTerm: fieldTerm, shape: tileShape, size: honeycomb ? 'single' : field.size }, `${String(field.position)}-${String(index)}`))), Array.from({ length: empties }, (_, index) => emptyPlace(`empty-${String(index)}`, fillerCount + index, 'single', {
                        'data-empty-index': index,
                    }))] })] }));
}
//# sourceMappingURL=FieldGrid.js.map